import React from 'react';
import {Plugins, ContextType, Atom} from '@packages/systems/wfdl';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {TListOfElement} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {getChildren} from '@packages/utilities/data-utils';
import {CLASS_NAME_LOCALES_LIST} from '../constants';
import {weakMemo} from '@packages/utilities/memo';
import type {LocaleItemContext} from '../types';

const getChildrenContext = weakMemo((context: ContextType) =>
  weakMemo((locale: LocaleItemContext) => context.set('locale', locale))
);

const LocalesList = Atom.createRender(
  ({context, attributes, classes, generateAttributes, data, renderChild}) => {
    const locales = context.get('locales') || [];

    if (locales.length < 1) {
      return null;
    }

    const listClasses = classes.push(CLASS_NAME_LOCALES_LIST);
    const listAttributes = attributes.set('role', 'list');
    const props = generateAttributes(listAttributes, listClasses);
    const children = getChildren(data);
    const child = children[0];

    return (
      <div {...props}>
        {locales.map((locale: LocaleItemContext, i: number) => {
          const newContext = getChildrenContext(context)(locale);
          return renderChild(child, newContext, `localesItem-${i}`, i);
        })}
      </div>
    );
  }
);

const config = createIntegratedAtom({
  displayName: 'Locales List',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Localization'), iconName('LocalesList')),
  },
  render: LocalesList,
  showEmpty: false,
  constraints: {
    ancestors: [
      {
        is: 'LocalesWrapper',
        rule: 'ExactlyOne',
      },
      {
        is: 'LocalesEmpty',
        rule: 'Forbid',
      },
    ],
    children: [
      {
        is: 'LocalesItem',
        name: 'Element',
        rule: 'RequireOnly',
      },
    ],
  },
  fields: {
    children: TListOfElement,
  },
});

export default config;
